import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from './useAuth';
import { ADMIN_EMAILS } from '../constants/pricing';

/**
 * Stato di accesso dell'utente corrente: approvato, email verificata, admin.
 *
 * L'approvazione è in `approvedUsers/{email}` (email minuscola come id). La
 * chiave è l'email e non l'uid perché si approva PRIMA che l'account esista:
 * chi chiede accesso non si è ancora registrato.
 *
 * L'admin passa sempre, per due vie indipendenti — `role` sul documento utente
 * (scritto dalla Cloud Function) e ADMIN_EMAILS lato client — così se una delle
 * due manca l'altra tiene e non ci si chiude fuori da soli.
 */
export function useApproval() {
  const { user, loading: authLoading } = useAuth();
  const [approved, setApproved] = useState(false);
  const [loading, setLoading] = useState(true);

  const email = (user?.email || '').toLowerCase();
  const isAdminByEmail = ADMIN_EMAILS.map((e) => e.toLowerCase()).includes(email);

  useEffect(() => {
    if (authLoading) return undefined;
    if (!user || !email) {
      setApproved(false);
      setLoading(false);
      return undefined;
    }

    const unsub = onSnapshot(
      doc(db, 'approvedUsers', email),
      (snap) => {
        setApproved(snap.exists());
        setLoading(false);
      },
      (err) => {
        // Le rules negano la lettura di un documento altrui: qui si legge solo
        // il proprio, quindi un errore significa "non approvato", non "rotto".
        console.warn('useApproval snapshot error:', err?.message || err);
        setApproved(false);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [user, email, authLoading]);

  // emailVerified va riletto dall'oggetto utente: dopo il clic sul link di
  // verifica il token in memoria resta indietro finché non si fa reload().
  const emailVerified = !!user?.emailVerified;

  return {
    loading: authLoading || loading,
    isAdmin: isAdminByEmail,
    approved: approved || isAdminByEmail,
    emailVerified: emailVerified || isAdminByEmail,
    // Passa solo chi è approvato E ha dimostrato di possedere l'indirizzo.
    canEnter: isAdminByEmail || (approved && emailVerified),
  };
}
